import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BootstrapTable from 'react-bootstrap-table-next';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import ToggleButton from 'react-bootstrap/ToggleButton';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Alert from 'react-bootstrap/Alert';
import Header from '../../components/Header';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import SearchableSelect from '../../components/SearchableSelect';
import EulaAgreementModal from '../../components/EulaAgreementModal';
import AsyncStatus from '../../components/AsyncStatus';
import { ventrisConfig } from '../../ventris.config';

import AddStudentIcon from '../../icons/AddStudentIcon';
import CreateGroupIcon from '../../icons/CreateGroupIcon';
import LaunchTestIcon from '../../icons/LaunchTestIcon';
import GenerateReportIcon from '../../icons/GenerateReportIcon';
import StudentIcon from '../../icons/StudentIcon';
import TestIcon from '../../icons/TestIcon';
import { getUseCaseTerminology } from '../../useCase';
import { completedTestDestination, launchableAssessmentsForUser, userHasResultsAccess } from '../../assessmentAccess';

type ClinicianHomeProps = {
};

type ViewerMode = 'Examiner' | 'Student';
const showDeviceModeToggle = false;

function ClinicianHome({}: ClinicianHomeProps) {
  const terms = getUseCaseTerminology();
  const [user, setUser] = useState([] as any);
  const [students, setStudents] = useState([] as any);
  const [student, setStudent] = useState("" as any);
  const [test, setTest] = useState(3 as any);
  const [recentStudents, setRecentStudents] = useState([] as any);
  const [recentTests, setRecentTests] = useState([] as any);
  const [studentTests, setStudentTests] = useState([] as any);
  const [studentTest, setStudentTest] = useState("" as any);
  const [showLaunchTest, setShowLaunchTest] = useState(false);
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [viewerMode, setViewerMode] = useState<ViewerMode>('Examiner');
  const [joinCode, setJoinCode] = useState("");
  const [joinCodeError, setJoinCodeError] = useState("");
  const [joinCodeLoading, setJoinCodeLoading] = useState(false);

  useEffect(() => {
    let token = sessionStorage.getItem("token");
    let currentUser = sessionStorage.getItem("current_user");
    const parsedUser = JSON.parse(currentUser!);
    setUser(parsedUser);
    const firstAssessment = launchableAssessmentsForUser(parsedUser)[0];
    if (firstAssessment) setTest(firstAssessment.schemaId);

    fetch(ventrisConfig.url + '/api/students?sizePerPage=1000', {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setStudents(data.students);
      if (data.students.length > 0) {
        setStudent(data.students[0].id);
        loadTestsForStudent(data.students[0].id);
      }
    });

    fetch(ventrisConfig.url + '/api/students?sizePerPage=5', {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setRecentStudents(data.students);
    });

    fetch(ventrisConfig.url + '/api/tests?sizePerPage=5', {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setRecentTests(data.tests);
    });
  }, []);

  const loadTestsForStudent = (studentId: any) => {
    let token = sessionStorage.getItem("token");
    
    fetch(ventrisConfig.url + '/api/tests?sizePerPage=1000&test=3&student=' + studentId, {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setStudentTests(data.tests);
      if (data.tests?.length > 0) {
        setStudentTest(data.tests[0].id);
      } else {
        setStudentTest("");
      }
    });
  }

  const loadCompletedTestsForStudent = (studentId: any) => {
    let token = sessionStorage.getItem("token");
    
    fetch(ventrisConfig.url + '/api/tests?sizePerPage=1000&test=3&student=' + studentId, {
      method: 'get',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      }
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      setStudentTests(data.tests);
      setStudentTest("-1");
    });
  }

  const joinExistingTest = () => {
    if (studentTest !== "" && studentTest !== null) {
      document.location.href = "/student-test/WRA/" + studentTest;
    }
  }

  const joinExistingTestByCode = async () => {
    const normalizedCode = joinCode.trim().toUpperCase();
    if (normalizedCode === "") {
      setJoinCodeError("Please enter a test code.");
      return;
    }

    setJoinCodeError("");
    setJoinCodeLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(ventrisConfig.url + '/api/test/code/' + encodeURIComponent(normalizedCode), {
        method: 'get',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        }
      });

      const data = await response.json();
      if (!data.success || !data.test) {
        setJoinCodeError(data.message || "We couldn't find a test with that code.");
        return;
      }

      if (data.test.name !== "WRA") {
        setJoinCodeError("Only WRA tests can be joined in student mode.");
        return;
      }

      document.location.href = "/student-test/WRA/" + data.test.id;
    } catch (error) {
      console.error(error);
      setJoinCodeError("An error occurred while looking up that test code.");
    } finally {
      setJoinCodeLoading(false);
    }
  }

  const startNewTest = () => {
    const id = Number(test);
    const assessment = launchableAssessmentsForUser(user).find((option) => option.schemaId === id);
    if (!assessment) return;
    const name = assessment.name;

    let token = sessionStorage.getItem("token");
    fetch(ventrisConfig.url + '/api/test', {
      method: 'post',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        name: name,
        status: "Incomplete",
        test_schema_id: id,
        organization_id: user.organization_id,
        student_id: student,
      })
    }).then(function (response) {
      return response.json();
    }).then(async function (data) {
      if (data.success && data.test?.id) {
        document.location.href = "/test/" + name + "/" + data.test.id;
        return;
      }

      alert(data.message || "Unable to start a new test.");
    });
  }

  const viewTestResults = () => {
    document.location.href = "/results?students=" + encodeURIComponent(student) + "&test=" + encodeURIComponent(studentTest);
  }

  const loadTest = (name: any, testId: any) => {
    document.location.href = "/test/" + name + "/" + testId;
  }

  const studentNameFormatter = (cell: any, row: any) => {
    return (
      row.first_name + " " + row.last_name
    );
  }

  const studentActionFormatter = (cell: any, row: any) => {
    return (
      <div className="table-actions">
        <Button variant="link" size="sm" onClick={() => {setStudent(row.id); setShowLaunchTest(true);}}>Launch a Test</Button>
      </div>
    );
  }

  const studentColumns = [
    {
      dataField: 'last_name',
      text: 'Name',
      sort: true,
      formatter: studentNameFormatter
    },
    {
      dataField: 'organization',
      text: 'Organization',
      sort: true
    },
    {
      dataField: 'grade',
      text: 'Grade',
      sort: true
    },
    {
      dataField: 'id',
      text: 'Actions',
      formatter: studentActionFormatter
    }
  ];

  const testActionFormatter = (cell: any, row: any) => {
    return (
      <div className="table-actions">
        <Button variant="link" size="sm" onClick={() => row.status === "Complete" ? document.location.href = completedTestDestination(row) : loadTest(row.name, row.id)}>{row.status === "Complete" ? "Show Results" : "Continue Test"}</Button>
      </div>
    );
  }

  const dateFormatter = (cell: any, row: any) => {
    console.log(cell);
    return (
      <div>
        {(new Date(Date.parse(cell))).toLocaleDateString()}
      </div>
    );
  }

  const updatedFormatter = (updatedAt: any) => {
    if (!updatedAt) {
      return "";
    }

    const parsedDate = new Date(updatedAt);
    if (isNaN(parsedDate.getTime())) {
      return updatedAt;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();

    let hours = parsedDate.getHours();
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hoursString = String(hours).padStart(2, "0");

    return `${month}/${day}/${year} ${hoursString}:${minutes} ${ampm}`;
  }

  const testColumns = [
    {
      dataField: 'name',
      text: 'Test',
      sort: true
    },
    {
      dataField: 'student',
      text: terms.student,
      sort: true
    },
    {
      dataField: 'clinician',
      text: terms.teacher,
      sort: true
    },
    {
      dataField: 'completed_timestamp',
      text: 'Administration Date',
      formatter: dateFormatter
    },
    {
      dataField: 'id',
      text: 'Actions',
      formatter: testActionFormatter
    }
  ];

  const viewerModeOptions: { label: string; value: ViewerMode }[] = [
    { label: 'Examiner', value: 'Examiner' },
    { label: terms.student, value: 'Student' }
  ];

  return (
    <Container fluid className="ventris-content-wrapper">
      <Header portal="clinician" page="home" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
              <div className="flex flex-row flex-gap">
                <div className="flex-grow">
                  <h1 className="h2">Welcome, {user.first_name}!</h1>
                  <p>Use the top navigation bar to explore {terms.students.toLowerCase()}, {terms.classes.toLowerCase()}, groups, and tests or edit your profile. Or, select a quick action below!</p>
                </div>
                {showDeviceModeToggle ? <div className="flex-grow flex flex-column">
                  <div className="mode-switch-title">
                    DEVICE MODE:
                  </div>
                  <ButtonGroup className="mode-switch-pill" aria-label="Portal mode selection">
                    {viewerModeOptions.map((option) => (
                      <ToggleButton
                        key={option.value}
                        id={`viewer-mode-${option.value}`}
                        type="radio"
                        variant="light"
                        name="viewer-mode"
                        value={option.value}
                        checked={viewerMode === option.value}
                        onChange={(event) => setViewerMode(event.currentTarget.value as ViewerMode)}
                        className="mode-switch-button"
                      >
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </div> : null}
              </div>
              <br />
              {viewerMode === "Student" ?
              <>
                <h3>{terms.student} Mode Actions (WRA Only)</h3>
                <p>Please select the {terms.student.toLowerCase()} you wish to test, and the test you wish to join. Please ensure that you have already started the test on your device. This will sync the two devices.</p>
                <br />
                <Card className="student-mode-card">
                  <div className="flex flex-column flex-grow">
                    <Form.Group className="mb-3 ventris-form-element" controlId="student-mode-student">
                      <Form.Label>{terms.student}:</Form.Label>
                      <SearchableSelect
                        ariaLabel={terms.student}
                        options={students.map((student: any) => ({
                          value: student.id,
                          label: student.student_code
                        }))}
                        value={student}
                        onChange={(value) => { setStudent(value); loadTestsForStudent(value); }}
                        placeholder={`Select ${terms.student.toLowerCase()}`}
                        searchPlaceholder={`Search ${terms.students.toLowerCase()}...`}
                      />
                    </Form.Group>
                  </div>
                  <div className="flex flex-column flex-grow">
                    <Form.Group className="mb-3 ventris-form-element" controlId="student-mode-test">
                      <Form.Label>Test:</Form.Label>
                      <Form.Select required aria-label="Test" value={studentTest} onChange={(event) => setStudentTest(event.target.value)}>
                        {studentTests.map((test: any) => <option key={test.id} value={test.id}>{test.name} - {test.clinician} - {updatedFormatter(test.updated_at)}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal" onClick={() => joinExistingTest()}>Join Test in Progress</Button>
                </Card>
                <div className="student-mode-or">OR</div>
                <Card className="student-mode-card student-mode-card-code" aria-busy={joinCodeLoading}>
                  <AsyncStatus busy={joinCodeLoading} busyMessage="Joining test." completeMessage="Test request completed." />
                  <div className="flex flex-column flex-grow">
                    <Form.Group className="mb-0 ventris-form-element" controlId="student-mode-test-code">
                      <Form.Label>Test Code:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter test code"
                        value={joinCode}
                        onChange={(event) => {
                          setJoinCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""));
                          setJoinCodeError("");
                        }}
                      />
                    </Form.Group>
                    {joinCodeError ? <Alert variant="danger" className="student-mode-code-error">{joinCodeError}</Alert> : null}
                  </div>
                  <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal" onClick={() => joinExistingTestByCode()} disabled={joinCodeLoading || joinCode.trim().length === 0}>
                    {joinCodeLoading ? "Joining..." : "Join by Code"}
                  </Button>
                </Card>
              </> :
              <>
                <h3>Quick Actions</h3>
                <br />
                <div className="quick-actions">
                  <div className="quick-action quick-action-purple">
                    <div className="flex-grow"></div>
                    <AddStudentIcon />
                    <div className="flex-grow"></div>
                    <div className="quick-action-footer">
                      <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-purple full-width" onClick={() => document.location.href = "/students"}>Add a {terms.student}</Button>
                    </div>
                  </div>
                  <div className="quick-action quick-action-teal">
                    <div className="flex-grow"></div>
                    <CreateGroupIcon />
                    <div className="flex-grow"></div>
                    <div className="quick-action-footer">
                      <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-teal full-width" onClick={() => document.location.href = "/groups"}>Create a Group</Button>
                    </div>
                  </div>
                  <div className="quick-action quick-action-green">
                    <div className="flex-grow"></div>
                    <LaunchTestIcon />
                    <div className="flex-grow"></div>
                    <div className="quick-action-footer">
                      <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-green full-width" onClick={() => setShowLaunchTest(true)}>Launch a Test</Button>
                    </div>
                  </div>
                  {userHasResultsAccess(user) ? <div className="quick-action quick-action-orange">
                    <div className="flex-grow"></div>
                    <GenerateReportIcon />
                    <div className="flex-grow"></div>
                    <div className="quick-action-footer">
                      <Button variant="secondary" className="ventris-button-secondary ventris-button-secondary-orange full-width" onClick={() => { setStudentTest("-1"); setShowGenerateReport(true); }}>View Test Results</Button>
                    </div>
                  </div> : null}
                </div>
                <br /><br />
                <h3>Recent Activity</h3>
                <br />
                <div className="recent-activity">
                  <Card className="recent-activity-card recent-activity-card-purple">
                    <div className="recent-activity-card-header">
                      <StudentIcon />
                      <div className="flex-grow">
                        Recent {terms.students}
                      </div>
                      <Button variant="link" size="sm" onClick={() => document.location.href = '/students'}>View All {terms.students}</Button>
                    </div>
                    <ResponsiveTableRegion label={`Recent ${terms.students}`}>
                      <BootstrapTable keyField='id' data={recentStudents} columns={studentColumns} bordered={false} />
                    </ResponsiveTableRegion>
                  </Card>
                  <Card className="recent-activity-card recent-activity-card-green">
                    <div className="recent-activity-card-header">
                      <TestIcon />
                      <div className="flex-grow">
                        Recent Tests
                      </div>
                      <Button variant="link" size="sm" onClick={() => document.location.href = '/tests'}>View All Tests</Button>
                    </div>
                    <ResponsiveTableRegion label="Recent tests">
                      <BootstrapTable keyField='id' data={recentTests} columns={testColumns} bordered={false} />
                    </ResponsiveTableRegion>
                  </Card>
                </div>
              </> }
              <br /><br /><br />
          </Col>
        </Row>
      </Container>
      <EulaAgreementModal user={user} />
      <Offcanvas show={showLaunchTest} onHide={() => setShowLaunchTest(false)} placement="end" scroll={false} backdrop={true} aria-labelledby="home-launch-test-title">
          <Offcanvas.Header className="offcanvas-header-green" closeButton>
              <Offcanvas.Title id="home-launch-test-title">Launch a Test</Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <p>To start a new test, select a {terms.student.toLowerCase()} from the drop-down list, then click one of the green buttons below. If a {terms.student.toLowerCase()} doesn&apos;t appear in the list, go to {terms.students} to create one, then return to this screen.</p>
            <br />
            {students.length > 0 ?
            <>
              <Form.Group className="mb-3 ventris-form-element" controlId="home-launch-student">
                  <Form.Label>{terms.student}:</Form.Label>
                  <SearchableSelect
                    ariaLabel={terms.student}
                    options={[...students]
                      .sort((a: any, b: any) =>
                        (a.last_name || "").localeCompare(b.last_name || "") ||
                        (a.first_name || "").localeCompare(b.first_name || "")
                      )
                      .map((student: any) => ({
                        value: student.id,
                        label: `${student.last_name}, ${student.first_name} (${student.student_code})`
                      }))}
                    value={student}
                    onChange={setStudent}
                    placeholder={`Select ${terms.student.toLowerCase()}`}
                    searchPlaceholder={`Search ${terms.students.toLowerCase()}...`}
                  />
              </Form.Group>
              <Form.Group className="mb-3 ventris-form-element" controlId="home-launch-test">
                  <Form.Label>Test:</Form.Label>
                  <Form.Select required aria-label="Test" value={test} onChange={(event) => setTest(event.target.value)}>
                    {launchableAssessmentsForUser(user).map((assessment) => <option key={assessment.name} value={assessment.schemaId}>{assessment.name}</option>)}
                  </Form.Select>
              </Form.Group>
              <br /><br />
              <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal full-width" onClick={startNewTest}>Launch Test</Button>
            </> :
            <>
              Before you can start a test, you must add a {terms.student.toLowerCase()}:
              <br /><br />
              <Link to="/students" className="ventris-button-secondary ventris-button-secondary-teal full-width">Manage {terms.students}</Link>
            </> }
          </Offcanvas.Body>
      </Offcanvas>
      <Offcanvas show={showGenerateReport} onHide={() => setShowGenerateReport(false)} placement="end" scroll={false} backdrop={true} aria-labelledby="home-results-title">
          <Offcanvas.Header className="offcanvas-header-orange" closeButton>
              <Offcanvas.Title id="home-results-title">View Test Results</Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <p>To view test results, select a {terms.student.toLowerCase()} and test in the drop-down lists below, then click the "View Test Results" button.</p>
            <br />
            {students.length > 0 ?
            <>
              <Form.Group className="mb-3 ventris-form-element" controlId="home-results-student">
                  <Form.Label>{terms.student}:</Form.Label>
                  <SearchableSelect
                    ariaLabel={terms.student}
                    options={students.map((student: any) => ({
                      value: student.id,
                      label: student.student_code
                    }))}
                    value={student}
                    onChange={(value) => { setStudent(value); loadCompletedTestsForStudent(value); }}
                    placeholder={`Select ${terms.student.toLowerCase()}`}
                    searchPlaceholder={`Search ${terms.students.toLowerCase()}...`}
                  />
              </Form.Group>
              <Form.Group className="mb-3 ventris-form-element" controlId="home-results-test">
                <Form.Label>Test:</Form.Label>
                <Form.Select required aria-label="Test" value={studentTest} onChange={(event) => setStudentTest(event.target.value)}>
                  <option value="-1">All</option>
                  {studentTests.map((test: any) => 
                    <>
                      {test.status === "Complete" ? <option value={test.id}>{test.name} - {test.clinician} - {updatedFormatter(test.updated_at)}</option> : null}
                    </>
                  )}
                </Form.Select>
              </Form.Group>
              <br /><br />
              <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal full-width" onClick={viewTestResults}>View Test Results</Button>
            </> :
            <>
              Before you can generate a report, you must add a {terms.student.toLowerCase()}:
              <br /><br />
              <Link to="/students" className="ventris-button-secondary ventris-button-secondary-teal full-width">Manage {terms.students}</Link>
            </> }
          </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

export default ClinicianHome;
